import type { PartDefinition } from "@rogue/rackbuilder"
import { MMS } from "../../modelPaths"
import type { StorageChainConfig, MassStorageOptions } from "../../RackTypes"
import { findBundleProduct } from "../rackbuilder-util"
import { getShelfSelection } from "../selection-utils"
import type { ShelfData } from "./hardware-counting-utils"
import type { IRogue, BuyRequest } from "@/js/services/RogueTypes"

function createShelfPart(selection: string | undefined, width: string): PartDefinition[] {
  let shelf: PartDefinition[] = []

  if (selection && width) {
    shelf = [{
      mesh   : `${MMS}#${selection} ${width}`,
      slots  : {},
      anchor : 'left',
      offset : width === '43' ? {} : { x: 0.1 },
    }]
  }

  return shelf
}

export function extractNamedShelfSelections(rogue: IRogue, buyRequest: BuyRequest): Record<string, string|undefined> {
  const result: Record<string, string|undefined> = {}
  const names = ['One', 'Two', 'Three', 'Four', 'Five']

  for (let i = 0; i < names.length; i += 1) {
    const name = names[i]
    const index  = i + 1

    const corner = findBundleProduct(rogue, buyRequest, 'corner', true)?.name
    if (corner) {
      result[`shelf${name}Selection`] = 'corner'
    } else {
      const shelfBundle = findBundleProduct(rogue, buyRequest, `shelf ${index}`, true)
      result[`shelf${name}Selection`] = getShelfSelection(shelfBundle?.name)
    }
  }

  return result
}

export function getShelfSpacing(selection: string | undefined): number {
  if (!selection) return 18
  const name = selection.toLowerCase()
  if (name.includes('medball')) return 22
  if (name.includes('kettlebell') || name.includes('dumbbell')) return 18

  return 18
}

export function getMaxZByHeight(height: string): number {
  // These values should match the maximum allowed height per rack configuration
  if (height === '100') return 115
  if (height === '70') return 88

  return 66
}

export function isPositionBlocked(startZ: number, height: number, occupiedRanges: Array<{start: number, end: number}>): boolean {
  const endZ = startZ + height

  return occupiedRanges.some(range => (startZ < range.end && endZ > range.start))
}

export function shouldHideDumbbellShelf(heightSelection: string, shelfOneSelection?: string, shelfTwoSelection?: string, shelfThreeSelection?: string, shelfFourSelection?: string, shelfFiveSelection?: string):'hide' | 'replace' | 'none' {
  const isMedballIn1 = shelfOneSelection?.toLowerCase().includes('medball')
  const isMedballIn2 = shelfTwoSelection?.toLowerCase().includes('medball')
  const isMedballIn3 = shelfThreeSelection?.toLowerCase().includes('medball')
  const isMedballIn4 = shelfFourSelection?.toLowerCase().includes('medball')

  const isDumbbellIn3 = shelfThreeSelection?.toLowerCase().includes('dumbbell')
  const isDumbbellIn4 = shelfFourSelection?.toLowerCase().includes('dumbbell')
  const isDumbbellIn5 = shelfFiveSelection?.toLowerCase().includes('dumbbell')

  // DB shelf should never be on shelf 3 for a 43" unit
  if (heightSelection === '43' && isDumbbellIn3) {
    return 'replace'
  }

  if (heightSelection === '70' && isDumbbellIn4) {
    return (isMedballIn1 && isMedballIn2 && isMedballIn3) ? 'hide' : 'none'
  }

  if (heightSelection === '100' && isDumbbellIn5) {
    return (isMedballIn1 || isMedballIn2 || isMedballIn3 || isMedballIn4) ? 'replace' : 'none'
  }

  return 'none'
}

export function assignShelfPlacement(shelfTarget: PartDefinition, shelfConfig: StorageChainConfig, config: MassStorageOptions, isAdjacentTo100?: boolean) {
  const shelfConfigs = [
    { id: 'shelf_one', selection: shelfConfig.shelfOneSelection, condition: true, range: [0, 0] },
    { id: 'shelf_two', selection: shelfConfig.shelfTwoSelection, condition: true, range: [18, 22] },
    { id: 'shelf_three', selection: shelfConfig.shelfThreeSelection, condition: true, range: [36, 44] },
    { id: 'shelf_four', selection: shelfConfig.shelfFourSelection, condition: shelfConfig.heightSelection === '70' || shelfConfig.heightSelection === '100', range: [54, 62] },
    { id: 'shelf_five', selection: shelfConfig.shelfFiveSelection, condition: shelfConfig.heightSelection === '100', range: [72, 92] },
  ]

  const maxZ = getMaxZByHeight(shelfConfig.heightSelection)
  const unitShelves: ShelfData[] = []

  // Keep track of occupied positions
  const occupiedRanges: Array<{start: number, end: number}> = []

  shelfConfigs.forEach(({ id, selection, condition, range }, index) => {
    if (!condition || !selection) return

    const shelfHeight = getShelfSpacing(selection)
    let targetZ = range[0] // Start with preferred position

    if (index === 0 && isAdjacentTo100) {
      targetZ += 4
    }

    // If the preferred position is blocked, find the lowest available position
    while (isPositionBlocked(targetZ, shelfHeight, occupiedRanges) && targetZ <= maxZ) {
      targetZ += 1 // Move up by 1 unit until we find space
    }

    let slotName = `${id}_${targetZ}h`
    const is43Height = shelfConfig.heightSelection === '43'
    const is70Height =  shelfConfig.heightSelection === '70'
    const is100Height = shelfConfig.heightSelection === '100'
    const slotZ = targetZ
    const offsetZ = is100Height ? 0 : 0
    let hardwareSlotName = `${id}_${slotZ + offsetZ}h`
    const shelfType = getShelfSelection(selection)

    // Prevent placing this shelf if it would exceed the total allowed height unless it's the last shelf on the 43" and 70" unit heights
    if (targetZ + shelfHeight > maxZ && !(is43Height && slotName.includes('shelf_three') || is70Height && slotName.includes('shelf_four'))) {
      return
    } 
    
    occupiedRanges.push({ start: targetZ, end: targetZ + shelfHeight })

    const dumbbellAction = shouldHideDumbbellShelf(
      shelfConfig.heightSelection,
      shelfConfig.shelfOneSelection,
      shelfConfig.shelfTwoSelection,
      shelfConfig.shelfThreeSelection,
      shelfConfig.shelfFourSelection,
      shelfConfig.shelfFiveSelection
    )

    if (dumbbellAction === 'replace') {
      if ((is43Height && id === 'shelf_three') || (is70Height && id === 'shelf_four') || (is100Height && id === 'shelf_five')) {
        selection = 'kettlebell shelf'
      }
    } else if (dumbbellAction === 'hide') {
      if (is70Height && id === 'shelf_four') {
        selection = undefined
      }
    }

    // Reassign the slots for the last shelf on 43" and 70" heights since spacing rules are ignored
    if (is43Height && slotName.includes('shelf_three')) {
      slotName = 'shelf_three_36h'
      hardwareSlotName = 'shelf_three_36h'
    } else if (is70Height && slotName.includes('shelf_four') && !shelfType?.includes('dumbbell')) {
      slotName = 'shelf_four_62h'
      hardwareSlotName = 'shelf_four_62h'
    }

    unitShelves.push({ hardwareSlotName, shelfType })
    shelfTarget.slots[slotName] = createShelfPart(selection, shelfConfig.widthSelection || config.width)
  })

  return unitShelves
}
